import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type AppUpdate = {
  ios: string;
  android: string;
  ios_url: string;
  android_url: string;
  configured: boolean;
  updated_at: string | null;
};

const ENDPOINT = "/api/v1/admin/app-update";
const VERSION = /^\d+(\.\d+){1,2}$/;

export function AppUpdatePage() {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ["app-update"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load app update settings");
      return response.json() as Promise<AppUpdate>;
    },
  });

  const [form, setForm] = React.useState({ ios: "", android: "", ios_url: "", android_url: "" });
  const [loadedAt, setLoadedAt] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!current.data) return;
    setForm({
      ios: current.data.ios,
      android: current.data.android,
      ios_url: current.data.ios_url,
      android_url: current.data.android_url,
    });
    setLoadedAt(current.data.updated_at);
  }, [current.data]);

  const save = useMutation({
    mutationFn: async (body: typeof form) => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, {
        method: "PUT",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Save failed");
      return payload as AppUpdate;
    },
    onSuccess: () => {
      toast.success("Minimum version saved. Users below it will be asked to update.");
      queryClient.invalidateQueries({ queryKey: ["app-update"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const problems: string[] = [];
  if (form.ios && !VERSION.test(form.ios)) problems.push("iOS version must look like 1.7.2");
  if (form.android && !VERSION.test(form.android)) problems.push("Android version must look like 1.7.2");
  if (!form.ios && !form.android) problems.push("Enter at least one minimum version");
  if (!/^https:\/\/\S+$/.test(form.ios_url)) problems.push("iOS store link must start with https://");
  if (!/^https:\/\/\S+$/.test(form.android_url)) problems.push("Android store link must start with https://");

  const active = current.data && (current.data.ios || current.data.android);

  return (
    <div className="space-y-6">
      <PageHeader title="App update" description="Set the minimum app version. Users on an older version must update before they can use the app." />

      <Card className="rounded-2xl">
        <CardContent className="flex flex-col gap-2 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current requirement</p>
            {current.isLoading ? (
              <Skeleton className="mt-2 h-5 w-64" />
            ) : (
              <p className="mt-1 text-sm">
                {active
                  ? `iOS ${current.data?.ios || "—"} · Android ${current.data?.android || "—"}`
                  : "No minimum version set. All installed versions can use the app."}
              </p>
            )}
          </div>
          {loadedAt && <p className="text-xs text-muted-foreground">Last changed {new Date(loadedAt).toLocaleString("en-GB")}</p>}
        </CardContent>
      </Card>

      {current.isError ? (
        <Card className="rounded-2xl border-destructive/40">
          <CardContent className="p-5 text-sm text-destructive">The current requirement could not be loaded.</CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl">
          <CardContent className="space-y-6 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Minimum iOS version</Label>
                <Input value={form.ios} onChange={(e) => setForm({ ...form, ios: e.target.value.trim() })} placeholder="1.7.2" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Minimum Android version</Label>
                <Input value={form.android} onChange={(e) => setForm({ ...form, android: e.target.value.trim() })} placeholder="1.7.2" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">App Store link</Label>
                <Input value={form.ios_url} onChange={(e) => setForm({ ...form, ios_url: e.target.value.trim() })} placeholder="https://apps.apple.com/…" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Google Play link</Label>
                <Input value={form.android_url} onChange={(e) => setForm({ ...form, android_url: e.target.value.trim() })} placeholder="https://play.google.com/…" />
              </div>
            </div>

            <div className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
              Leave a version empty to turn the requirement off for that platform. Users whose installed version is lower than the minimum will see an update screen with the store link.
            </div>

            {problems.length > 0 && (
              <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}

            <Button
              className="rounded-xl"
              disabled={save.isPending || problems.length > 0}
              onClick={() => save.mutate(form)}
            >
              {save.isPending ? "Saving…" : "Save minimum version"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
